import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function FinalExpenseInsuranceIndianaSeniors() {
  const SITE_URL = 'https://hoosierlifeinsurance.com';
  useEffect(() => {
    document.title = 'Final Expense Insurance for Indiana Seniors';
    
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Final expense insurance for Indiana seniors — compare burial insurance costs, top carriers, and plans for over 60, 70, and 80. No medical exam options available.');

    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', 'final expense insurance Indiana seniors, burial insurance Indiana, senior life insurance Indiana');

    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}/resources/final-expense-insurance-indiana-seniors`;

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Final Expense Insurance for Indiana Seniors: Complete Guide',
        description: 'Final expense insurance ensures your family won\'t face financial burden after you\'re gone. Learn how Indiana seniors can secure affordable coverage with no medical exam required.',
        author: {
          '@type': 'Person',
          name: 'Benjamin Wright'
        },
        publisher: {
          '@type': 'Organization',
          name: 'Hoosier Life Insurance',
          url: SITE_URL
        },
        datePublished: '2025-01-05T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: `${SITE_URL}/resources/final-expense-insurance-indiana-seniors`,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/resources/final-expense-insurance-indiana-seniors`
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_URL
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Resources',
            item: `${SITE_URL}/resources`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'Final Expense Insurance for Indiana Seniors',
            item: `${SITE_URL}/resources/final-expense-insurance-indiana-seniors`
          }
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Final Expense Insurance for Indiana Seniors: Complete Guide',
        description: 'Final expense insurance ensures your family won\'t face financial burden after you\'re gone. Learn how Indiana seniors can secure affordable coverage with no medical exam required.',
        url: `${SITE_URL}/resources/final-expense-insurance-indiana-seniors`,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          url: SITE_URL,
          name: 'Hoosier Life Insurance'
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Can I get final expense insurance if I have diabetes, heart disease, or cancer?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. Guaranteed issue final expense policies accept everyone regardless of health conditions including diabetes, heart disease, and cancer. You cannot be declined. You will pay slightly higher premiums than simplified issue policies and there is typically a 2-year waiting period before the full death benefit pays out, but acceptance is guaranteed for Indiana seniors ages 50-85.'
            }
          },
          {
            '@type': 'Question',
            name: 'Will my final expense insurance premiums increase as I get older?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'No. Final expense insurance has level premiums that are locked in for life at the rate you qualify for when you apply. Your monthly cost will never increase, regardless of your age or any changes in your health after the policy is issued. This makes it easy to budget for coverage on a fixed income.'
            }
          },
          {
            '@type': 'Question',
            name: 'Can my family use the final expense death benefit for anything, or just funeral costs?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Your beneficiary receives a tax-free lump sum and can use it for absolutely anything—funeral costs, burial, medical bills, credit card debt, outstanding loans, or they can simply keep it. There are no restrictions on how the death benefit is used. The money is paid directly to your named beneficiary, not to the funeral home or any other creditor.'
            }
          },
          {
            '@type': 'Question',
            name: 'If I already pre-paid my funeral, do I still need final expense insurance?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, pre-paid funeral plans only cover funeral home services. They do not cover burial plots, headstones, obituary costs, flowers, reception expenses, outstanding medical bills, probate fees, or other end-of-life costs. Final expense insurance fills those gaps. A typical Indiana funeral with burial totals $8,000-$12,000, and a pre-paid plan usually only covers a portion of that.'
            }
          },
          {
            '@type': 'Question',
            name: 'Will final expense insurance affect my Medicaid or Social Security benefits?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'No. Life insurance policies do not count as countable assets for Medicaid eligibility purposes, and paying premiums does not affect Social Security benefit amounts. Your final expense coverage is completely separate from these government programs. However, if you plan to apply for Medicaid in the future, it is worth consulting with an elder law attorney about how larger policies may be treated under Indiana\'s Medicaid asset rules.'
            }
          }
        ]
      }
    ];

    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-resource-final-expense-${index}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      schemas.forEach((_, index) => {
        const script = document.getElementById(`schema-resource-final-expense-${index}`);
        if (script) {
          document.head.removeChild(script);
        }
      });
    };
  }, [SITE_URL]);

  return (
    <>
      <title>Final Expense Insurance for Indiana Seniors | Burial Coverage Guide | Hoosier Life Insurance</title>
      <article className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white py-20">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-4xl mx-auto px-6">
            <div className="mb-6">
              <Link to="/resources" className="inline-flex items-center text-white/70 hover:text-white transition-colors text-sm">
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Resources
              </Link>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Final Expense Insurance for Indiana Seniors — No Exam, Guaranteed Options</h1>
            <p className="text-xl text-white/80 mb-8">Protect your family from funeral costs with affordable burial insurance. No medical exam required, guaranteed acceptance available.</p>
            <div className="flex flex-wrap gap-4 text-sm text-white/70">
              <span><i className="ri-calendar-line mr-2"></i>Updated January 2026</span>
              <span><i className="ri-time-line mr-2"></i>9 min read</span>
              <span><i className="ri-map-pin-line mr-2"></i>Indiana Seniors Guide</span>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              The average funeral in Indiana costs <strong>$8,000-$12,000</strong>—and that's before burial plots, headstones, or memorial services. For many Indiana seniors, the thought of leaving this financial burden to their children is unbearable.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Final expense insurance (also called burial insurance or funeral insurance) is a small whole life policy designed specifically to cover end-of-life costs. It's affordable, easy to qualify for, and ensures your family won't have to scramble for money during an already difficult time.
            </p>
          </div>

          {/* Quick Facts */}
          <div className="bg-[#FDF6F0] border-l-4 border-[#D97642] p-6 mb-12 rounded-r-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
              <i className="ri-information-line text-[#D97642] mr-3 text-2xl"></i>
              Quick Facts About Final Expense Insurance
            </h3>
            <ul className="space-y-2 text-gray-800">
              <li className="flex items-start">
                <i className="ri-check-line text-[#D97642] mr-3 mt-1 text-xl"></i>
                <span><strong>Coverage amounts:</strong> $5,000-$50,000 (most Indiana seniors choose $10,000-$25,000)</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-[#D97642] mr-3 mt-1 text-xl"></i>
                <span><strong>Monthly cost:</strong> $30-$150 depending on age, health, and coverage amount</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-[#D97642] mr-3 mt-1 text-xl"></i>
                <span><strong>No medical exam:</strong> Most policies require only health questions (some require none at all)</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-[#D97642] mr-3 mt-1 text-xl"></i>
                <span><strong>Guaranteed acceptance:</strong> Available for seniors with serious health conditions</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-[#D97642] mr-3 mt-1 text-xl"></i>
                <span><strong>Permanent coverage:</strong> Lasts your entire life, premiums never increase</span>
              </li>
            </ul>
          </div>

          {/* What Does It Cover */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What Does Final Expense Insurance Cover?</h2>
            <p className="text-gray-700 mb-8">
              Final expense insurance pays a tax-free lump sum to your beneficiary (usually your children or spouse) to cover all end-of-life costs:
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-building-2-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Funeral Home Services</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-[#D97642] mr-2 mt-1"></i>
                    <span>Funeral director fees: $2,000-$3,000</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-[#D97642] mr-2 mt-1"></i>
                    <span>Embalming & preparation: $700-$1,200</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-[#D97642] mr-2 mt-1"></i>
                    <span>Viewing & visitation: $400-$800</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-[#D97642] mr-2 mt-1"></i>
                    <span>Memorial service: $500-$1,000</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-landscape-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Burial or Cremation</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-[#D97642] mr-2 mt-1"></i>
                    <span>Casket: $2,000-$5,000</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-[#D97642] mr-2 mt-1"></i>
                    <span>Burial plot (Indiana): $1,000-$3,000</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-[#D97642] mr-2 mt-1"></i>
                    <span>Headstone/marker: $1,000-$3,000</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-[#D97642] mr-2 mt-1"></i>
                    <span>Cremation (alternative): $1,500-$3,000</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-file-text-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Legal &amp; Administrative</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-[#D97642] mr-2 mt-1"></i>
                    <span>Death certificates: $20-$50 each</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-[#D97642] mr-2 mt-1"></i>
                    <span>Probate fees: $500-$2,000</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-[#D97642] mr-2 mt-1"></i>
                    <span>Estate settlement costs</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-[#D97642] mr-2 mt-1"></i>
                    <span>Outstanding medical bills</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-more-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Additional Expenses</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-[#D97642] mr-2 mt-1"></i>
                    <span>Flowers & obituary: $300-$800</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-[#D97642] mr-2 mt-1"></i>
                    <span>Transportation: $300-$600</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-[#D97642] mr-2 mt-1"></i>
                    <span>Reception/meal: $500-$1,500</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-[#D97642] mr-2 mt-1"></i>
                    <span>Travel for family members</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-xl p-6">
              <h4 className="text-xl font-bold mb-3">Total Average Cost in Indiana</h4>
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-3xl font-bold mb-2">$8,000</p>
                  <p className="text-[#D97642] text-sm">Basic Funeral + Cremation</p>
                </div>
                <div>
                  <p className="text-3xl font-bold mb-2">$12,000</p>
                  <p className="text-[#D97642] text-sm">Traditional Funeral + Burial</p>
                </div>
                <div>
                  <p className="text-3xl font-bold mb-2">$15,000+</p>
                  <p className="text-[#D97642] text-sm">Full Service + Premium Options</p>
                </div>
              </div>
            </div>
          </div>

          {/* Types of Final Expense Policies */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">3 Types of Final Expense Policies for Indiana Seniors</h2>
            
            <div className="space-y-6">
              {/* Simplified Issue */}
              <div className="bg-white border-2 border-[#D97642] rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1A3A52] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-list-3-line text-2xl"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">1. Simplified Issue (Most Popular)</h3>
                    <p className="text-gray-700 mb-4">
                      <strong>Best for:</strong> Seniors in average to good health
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="font-bold text-gray-900 mb-2">✅ Pros:</p>
                        <ul className="space-y-1 text-sm text-gray-700">
                          <li className="flex items-start">
                            <i className="ri-check-line text-[#D97642] mr-2 mt-1"></i>
                            <span>No medical exam required</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-[#D97642] mr-2 mt-1"></i>
                            <span>Immediate full coverage (day 1)</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-[#D97642] mr-2 mt-1"></i>
                            <span>Lowest premiums</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-[#D97642] mr-2 mt-1"></i>
                            <span>Approval in 24-48 hours</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 mb-2">❌ Cons:</p>
                        <ul className="space-y-1 text-sm text-gray-700">
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>Must answer health questions</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>Can be declined for serious conditions</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="bg-[#FDF6F0] rounded-lg p-4">
                      <p className="text-sm text-gray-700"><strong>Example:</strong> Age 70, $15,000 coverage = <strong className="text-[#D97642]">$65-$85/month</strong></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Guaranteed Issue */}
              <div className="bg-white border-2 border-gray-300 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">2. Guaranteed Issue (No Health Questions)</h3>
                    <p className="text-gray-700 mb-4">
                      <strong>Best for:</strong> Seniors with serious health conditions (diabetes, heart disease, cancer)
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="font-bold text-gray-900 mb-2">✅ Pros:</p>
                        <ul className="space-y-1 text-sm text-gray-700">
                          <li className="flex items-start">
                            <i className="ri-check-line text-gray-600 mr-2 mt-1"></i>
                            <span>No health questions at all</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-gray-600 mr-2 mt-1"></i>
                            <span>Cannot be declined (guaranteed acceptance)</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-gray-600 mr-2 mt-1"></i>
                            <span>Approval in minutes</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 mb-2">❌ Cons:</p>
                        <ul className="space-y-1 text-sm text-gray-700">
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>2-year waiting period (graded benefit)</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>Higher premiums (20-40% more)</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>Lower coverage limits ($25K max)</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-700"><strong>Example:</strong> Age 70, $15,000 coverage = <strong className="text-gray-600">$95-$125/month</strong></p>
                      <p className="text-xs text-gray-600 mt-2">*If you pass away in years 1-2, beneficiary receives premiums paid + 10% interest. Full benefit pays after year 2.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modified/Graded */}
              <div className="bg-white border-2 border-gray-300 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-2xl"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">3. Graded Benefit (Middle Ground)</h3>
                    <p className="text-gray-700 mb-4">
                      <strong>Best for:</strong> Seniors with moderate health issues who can answer some questions
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="font-bold text-gray-900 mb-2">✅ Pros:</p>
                        <ul className="space-y-1 text-sm text-gray-700">
                          <li className="flex items-start">
                            <i className="ri-check-line text-gray-600 mr-2 mt-1"></i>
                            <span>Easier to qualify than simplified issue</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-gray-600 mr-2 mt-1"></i>
                            <span>Lower premiums than guaranteed issue</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-gray-600 mr-2 mt-1"></i>
                            <span>Partial benefit in years 1-2</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 mb-2">❌ Cons:</p>
                        <ul className="space-y-1 text-sm text-gray-700">
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>Waiting period for full benefit</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>Still requires some health questions</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-700"><strong>Example:</strong> Age 70, $15,000 coverage = <strong className="text-gray-600">$75-$95/month</strong></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cost by Age */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What Indiana Seniors Actually Pay</h2>
            <p className="text-gray-700 mb-8">
              Here are real monthly premiums for Indiana seniors (simplified issue, non-smoker, good health):
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-6 py-4 text-left font-bold">Age</th>
                    <th className="px-6 py-4 text-left font-bold">$10,000 Coverage</th>
                    <th className="px-6 py-4 text-left font-bold">$15,000 Coverage</th>
                    <th className="px-6 py-4 text-left font-bold">$25,000 Coverage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 60</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$35/month</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$50/month</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$80/month</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 65</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$45/month</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$65/month</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$105/month</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 70</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$60/month</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$85/month</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$135/month</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 75</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$80/month</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$115/month</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$185/month</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 80</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$110/month</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$160/month</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$260/month</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-600 mt-4">*Rates for Indiana residents. Smokers pay 30-50% more. Guaranteed issue policies cost 20-40% more.</p>
          </div>

          {/* Real Indiana Story */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Real Indiana Story: How Final Expense Insurance Helped the Miller Family</h2>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-heart-line text-3xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Dorothy Miller, Age 72, Fort Wayne</h3>
                  <p className="text-gray-700">
                    Dorothy was a retired teacher living on Social Security ($1,800/month). She had $3,000 in savings and worried her three children would have to pay for her funeral.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">What Dorothy Did</h4>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                    <span>Purchased a <strong>$15,000 simplified issue policy</strong> (no medical exam)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                    <span>Monthly premium: <strong>$82/month</strong> (fit comfortably in her budget)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                    <span>Named her daughter as beneficiary</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                    <span>Pre-planned her funeral with a local Fort Wayne funeral home</span>
                  </li>
                </ul>
              </div>

              <div className="bg-teal-600 text-white rounded-xl p-6">
                <h4 className="text-xl font-bold mb-3">The Outcome</h4>
                <p className="text-teal-50 mb-4">
                  When Dorothy passed away 4 years later, her daughter received the $15,000 death benefit within 5 days. The money covered:
                </p>
                <ul className="space-y-2 text-teal-50">
                  <li className="flex items-start">
                    <i className="ri-check-line mr-3 mt-1 text-xl"></i>
                    <span>Funeral home services: $8,200</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line mr-3 mt-1 text-xl"></i>
                    <span>Burial plot & headstone: $3,800</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line mr-3 mt-1 text-xl"></i>
                    <span>Reception for family: $1,200</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line mr-3 mt-1 text-xl"></i>
                    <span>Remaining $1,800 helped with estate costs</span>
                  </li>
                </ul>
                <p className="mt-4 text-teal-100 text-sm italic">
                  "Mom gave us the gift of not having to worry about money during the hardest week of our lives. We could focus on celebrating her life instead of scrambling for cash." — Sarah Miller, daughter
                </p>
              </div>
            </div>
          </div>

          {/* How to Choose Coverage Amount */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">How Much Coverage Do You Need?</h2>
            <div className="bg-white border-2 border-[#D97642] rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Calculate Your Final Expense Needs</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Funeral home services</span>
                  <span className="font-bold text-gray-900">$2,000-$3,000</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Casket or cremation</span>
                  <span className="font-bold text-gray-900">$1,500-$5,000</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Burial plot (Indiana)</span>
                  <span className="font-bold text-gray-900">$1,000-$3,000</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Headstone/marker</span>
                  <span className="font-bold text-gray-900">$1,000-$3,000</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Flowers, obituary, reception</span>
                  <span className="font-bold text-gray-900">$800-$2,000</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Outstanding medical bills</span>
                  <span className="font-bold text-gray-900">$1,000-$5,000</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Legal/probate fees</span>
                  <span className="font-bold text-gray-900">$500-$2,000</span>
                </div>
                <div className="flex justify-between items-center pt-4 text-xl">
                  <span className="font-bold text-gray-900">RECOMMENDED COVERAGE</span>
                  <span className="font-bold text-teal-600">$10,000-$25,000</span>
                </div>
              </div>
            </div>
          </div>

          {/* Common Questions */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Common Questions from Indiana Seniors</h2>
            <div className="space-y-4">
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-question-line text-teal-600 mr-3 text-2xl"></i>
                  Can I get final expense insurance if I have diabetes, heart disease, or cancer?
                </h3>
                <p className="text-gray-700 text-sm pl-11">
                  <strong>Yes!</strong> Guaranteed issue policies accept everyone regardless of health conditions. You'll pay slightly higher premiums and have a 2-year waiting period, but you cannot be declined.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-question-line text-teal-600 mr-3 text-2xl"></i>
                  Will my premiums increase as I get older?
                </h3>
                <p className="text-gray-700 text-sm pl-11">
                  <strong>No.</strong> Final expense insurance has level premiums—your monthly cost is locked in for life and will never increase.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-question-line text-teal-600 mr-3 text-2xl"></i>
                  Can my family use the money for anything, or just funeral costs?
                </h3>
                <p className="text-gray-700 text-sm pl-11">
                  Your beneficiary receives a tax-free lump sum and can use it for <strong>anything</strong>—funeral costs, medical bills, credit card debt, or even keep it. There are no restrictions.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-question-line text-teal-600 mr-3 text-2xl"></i>
                  What if I already pre-paid my funeral? Do I still need insurance?
                </h3>
                <p className="text-gray-700 text-sm pl-11">
                  Pre-paid funeral plans only cover funeral home services. They don't cover burial plots, headstones, medical bills, probate fees, or other end-of-life costs. Final expense insurance fills those gaps.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-question-line text-teal-600 mr-3 text-2xl"></i>
                  Will this affect my Medicaid or Social Security benefits?
                </h3>
                <p className="text-gray-700 text-sm pl-11">
                  <strong>No.</strong> Life insurance policies do not count as assets for Medicaid eligibility, and premiums don't affect Social Security benefits.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-2xl p-8 mb-12">
            <h2 className="text-3xl font-bold mb-6">Get Your Free Final Expense Quote</h2>
            <p className="text-xl text-white/80 mb-8">
              See how affordable it is to protect your family from funeral costs. No medical exam required for most policies.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <Link 
                to="/final-expense" 
                className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Get Instant Quote
              </Link>
              <Link 
                to="/book-appointment" 
                className="bg-[#0F2A3F] text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-[#0A1F2E] transition-colors border-2 border-white/30 whitespace-nowrap"
              >
                Talk to a Specialist
              </Link>
            </div>
          </div>

          {/* Related Articles */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h2>

            {/* Product Pages — two-way authority signal */}
            <div className="mb-4">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Product Pages</p>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <Link to="/final-expense" className="group bg-white border-2 border-[#D97642] rounded-xl p-5 hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-hand-heart-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">Final Expense Insurance</h3>
                  <p className="text-xs text-gray-500">Get rates, compare plans, and apply today</p>
                </Link>
                <Link to="/no-medical-exam" className="group bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-[#D97642] hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-stethoscope-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">No Medical Exam Life Insurance</h3>
                  <p className="text-xs text-gray-500">Get approved in 24 hours without a doctor visit</p>
                </Link>
                <Link to="/whole-life" className="group bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-[#D97642] hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-infinity-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">Whole Life Insurance</h3>
                  <p className="text-xs text-gray-500">Permanent coverage with guaranteed cash value</p>
                </Link>
              </div>
            </div>

            {/* Articles */}
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Articles &amp; Guides</p>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/blog/burial-insurance-indiana" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-article-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">Burial Insurance in Indiana</h3>
                <p className="text-sm text-gray-600">How burial insurance works and what Indiana seniors pay</p>
              </Link>

              <Link to="/blog/life-insurance-seniors-over-60-indianapolis" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-elderly-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">Life Insurance for Seniors Over 60</h3>
                <p className="text-sm text-gray-600">Coverage options, costs, and health requirements after 60</p>
              </Link>

              <Link to="/resources/life-insurance-mistakes-indiana-families" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-error-warning-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">7 Mistakes Indiana Families Make</h3>
                <p className="text-sm text-gray-600">Avoid the most costly life insurance errors Indiana families face</p>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Questions About Final Expense Insurance?</h2>
            <p className="text-xl text-gray-700 mb-8">
              Our Indiana-based agents specialize in helping seniors find affordable burial insurance. Let's find the right policy for your needs and budget.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:3176482983" className="bg-[#1A3A52] text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-[#0F2A3F] transition-colors whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-phone-line mr-2"></i>
                Call (317) 648-2983
              </a>
              <Link to="/contact" className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-100 transition-colors border-2 border-[#1A3A52] whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-message-3-line mr-2"></i>
                Send a Message
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}